import {
  Box, Button, Columns, SidebarModal,
} from '@domain-group/suit-theme'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import useTemplatePage from '@hooks/useTemplatePage'

import useAssignTemplatePagePermissionsModal from './hooks/useAssignTemplatePagePermissionsModal'

import Agencies from './components/Agencies'
import BrandThemes from './components/BrandThemes'
import Providers from './components/Providers'
import Publications from './components/Publications'
import ServiceProducts from './components/ServiceProducts'
import StepNavigation from './components/StepNavigation/index'
import Services from './components/Services/Services'
import TemplateGroups from './components/TemplateGroups'
import TemplatePageDetails from './components/TemplatePageDetails'

import useFlipperKey from '@hooks/useFlipperKey'
import { TemplatePageModel } from '@domain-group/realhub-backend-api-types'

const MODAL_KEY = 'AssignTemplatePagePermissionsModal'

const customNextSteps = {
  serviceIds: 'providerIds',
  providerIds: 'serviceProductIds',
  serviceProductIds: 'agencyIds',
  agencyIds: 'publicationIds',
  publicationIds: 'brandThemeIds',
}

type ModalCallbacks = {
  bulkAssignTemplatePagePermissions: (payload: HandleCallbackActionParams) => void,
  closeModal: () => void,
}

export type AssignTemplatePagePermissionsModalPayload = {
  stepKey: string,
  templatePage: TemplatePageModel,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
  payload: AssignTemplatePagePermissionsModalPayload
}

const AssignTemplatePagePermissionsModal = (props: ModalProps) => {
  const { callbacks, showModal, payload } = props
  const { closeModal, bulkAssignTemplatePagePermissions } = callbacks || {}

  const modalPayload = payload
  const initStepKey = modalPayload?.stepKey
  const initTemplatePageId = modalPayload?.templatePage?.id

  const { isEnabled } = useFlipperKey("permissions_modal_v2")

  const assignTemplatePagePermissionsPayload = useAssignTemplatePagePermissionsModal({
    activeStepKey: initStepKey,
    customStepKeys: isEnabled ? customNextSteps : undefined,
    templatePageId: initTemplatePageId,
  })

  const {
    activeStepKey,
    callbacks: { preparePermissions, setCurrentStepKey },
    hasNextStep,
    nextStepKey,
    saveEnabled,
    templatePage,
  } = assignTemplatePagePermissionsPayload

  const templatePagePayload = useTemplatePage(templatePage)
  const { callbacks: { bulkUpdateTemplatePagePermissions: bulkUpdateFn } } = templatePagePayload

  // To be used with handleCallbackAction
  const bulkAssignTemplatePagePermissionsPayload = {
    callbacks: {
      action: bulkUpdateFn,
      afterAction: closeModal,
      buildActionParams: () => [preparePermissions()],
    },
    toastText: 'Permissions Updated',
  }

  return (
    <SidebarModal callbacks={callbacks} disableAnimation modalKey={MODAL_KEY} showModal={showModal} size="fullscreen">
      <SidebarModal.Header callbacks={callbacks} title="Assign" titleSecondLine="Permissions" />

      <SidebarModal.Content boxProps={{ backgroundColor: 'backgroundColor' }}>
        <Columns flexDirection={['column', 'column', 'row']}>
          <Columns.Main>
            {isEnabled && (
              <>
                <StepNavigation
                  assignTemplatePagePermissionsPayload={assignTemplatePagePermissionsPayload}
                  isHorizontalLayout={isEnabled}
                  showTickIcon={isEnabled}
                  newPermissionsSteps={isEnabled}
                />

                <TemplatePageDetails templatePage={templatePage} />
              </>
            )}

            {activeStepKey === 'agencyIds' && (
              <Agencies assignTemplatePagePermissionsPayload={assignTemplatePagePermissionsPayload} />
            )}

            {activeStepKey === 'brandThemeIds' && (
              <BrandThemes assignTemplatePagePermissionsPayload={assignTemplatePagePermissionsPayload} />
            )}

            {activeStepKey === 'providerIds' && (
              <Providers assignTemplatePagePermissionsPayload={assignTemplatePagePermissionsPayload} />
            )}

            {activeStepKey === 'publicationIds' && (
              <Publications assignTemplatePagePermissionsPayload={assignTemplatePagePermissionsPayload} />
            )}

            {activeStepKey === 'serviceProductIds' && (
              <ServiceProducts assignTemplatePagePermissionsPayload={assignTemplatePagePermissionsPayload} />
            )}

            {activeStepKey === 'templateGroupIds' && (
              <TemplateGroups assignTemplatePagePermissionsPayload={assignTemplatePagePermissionsPayload} />
            )}

            {activeStepKey === 'serviceIds' && (
              <Services assignTemplatePagePermissionsPayload={assignTemplatePagePermissionsPayload} />
            )}
          </Columns.Main>

          <Columns.Sidebar>
            {!isEnabled && (
              <>
                <StepNavigation
                  assignTemplatePagePermissionsPayload={assignTemplatePagePermissionsPayload}
                  isHorizontalLayout={isEnabled}
                  showTickIcon={isEnabled}
                />

                <TemplatePageDetails templatePage={templatePage} />
              </>
            )}
          </Columns.Sidebar>
        </Columns>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Box marginLeft="auto" width="auto">
          {hasNextStep && (
            <Button
              buttonStyle="primaryEdit"
              loadingBubbleColor="white"
              onClick={nextStepKey ? () => setCurrentStepKey(nextStepKey) : undefined}
              size="large"
              width="auto"
            >
              Next Step
            </Button>
          )}

          {(saveEnabled || isEnabled)  && (
            <Button
              buttonStyle="primaryCreate"
              loadingBubbleColor="white"
              // @ts-expect-error TS2345
              onClick={() => bulkAssignTemplatePagePermissions(bulkAssignTemplatePagePermissionsPayload)}
              marginLeft="medium"
              size="large"
              width="auto"
            >
              Save and Exit
            </Button>
          )}
        </Box>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <AssignTemplatePagePermissionsModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
